import type { components } from "../../api/types";

type Node = components["schemas"]["Node"];

// Force-simulated ring/topic layout.
//
// Faithful port of the Decision_Web_Graph.html mockup's initPositions() —
// the user explicitly wants this architecture, so we mirror it line-for-line:
//
//   - Ring radii [0, 160, 320, 480, 640, 800, 960] (concentric)
//   - Topics within each ring get an evenly-divided angular sector spanning
//     -0.9π to +0.9π (so the "back" of the circle stays empty, top-up reads naturally)
//   - Seed positions near each node's (ring, topic) target angle
//   - 400 iterations of 4 forces:
//       1. Pairwise repulsion 20000/d²
//       2. Edge attraction (dist - 150) * 0.015
//       3. Ring gravity diff * 0.03 toward ringRadius[ring]
//       4. Tangential topic-sector pull, strength = angleDiff * 8
//   - Velocities: applied with 0.25 step, damped by 0.65 each iteration
//
// Real-tree data quirks (vs. the mockup's hand-curated dataset):
//   - fields.topics is an ARRAY (singular `fields.topic` doesn't exist).
//     Pick the first element for placement; nodes with empty topics land
//     in a synthetic "_untopped" bucket so they don't all collapse to
//     one angle.
//   - fields.ring may be a number OR a string in JSON; coerce defensively.
//   - refs[].rel uses kebab-case for some relations (motivated-by, cites);
//     normalization is the consumer's job, not placement's.

export type Placement = {
  id: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  ring: number;
  topic: string;
  // Per-node target angle in radians. Each node within a topic gets its
  // own slot along the topic's sector arc rather than all sharing the
  // sector midpoint, so the topic-sector pull spreads them evenly
  // instead of collapsing them onto a single point. Slot index sorted
  // by node id within the (ring, topic) bucket for stability.
  targetAngle: number;
};

export type PlacementResult = {
  positions: Map<string, Placement>;
  rings: number[]; // unique rings present
  topicsByRing: Map<number, string[]>; // ring -> sorted topics in that ring
  ringRadii: number[];
  topicAngles: Map<string, number>; // "ring:topic" -> radians
};

// DEFAULT ring radii match the Decision_Web_Graph mockup: ring 0 = 0
// (root area = center; ring-0 nodes cluster around origin via repulsion
// equilibrium against ring-gravity), then 160px steps outward. These
// are the visual anchor — small/medium trees keep these defaults so
// the page reads consistently.
const DEFAULT_RING_RADII = [0, 160, 320, 480, 640, 800, 960] as const;
// Per-topic-sector arc-length each node consumes when sizing a ring.
// 15px is very tight — accepts node overlap at moderate density to
// keep the graph compact. Rings expand only when the densest topic
// genuinely overflows the default radius. The geometry-correct value
// for "no overlap" would be ~60 (node diameter) but at that target
// the rings balloon to 5x default even at modest counts; we accept
// overlap and let the topic-cluster blob handle visual grouping.
const NODE_ARC_PX = 15;
// Minimum radial gap between consecutive rings. 100 is just enough
// for the 80px-clamp annulus to not overlap with neighbors. Reduced
// from 120 to keep the overall radius small when ring 0 gets pushed
// outward.
const MIN_RING_SPACING = 100;
const ITERATIONS = 400;
const REPULSION = 20000;
const EDGE_REST_LENGTH = 150;
const EDGE_K = 0.015;
const RING_K = 0.03;
const TOPIC_K = 14;
const VELOCITY_STEP = 0.25;
const DAMPING = 0.65;
const SECTOR_SPAN = Math.PI * 1.8;
const SECTOR_START = -Math.PI * 0.9;
// Hard radial clamp per ring. Loosened to 80 (was 50) so nodes have
// more room to settle naturally; tighter clamps were forcing nodes
// into 100px-wide bands that didn't accommodate density. Outer
// rings still don't overlap because MIN_RING_SPACING (120) > 2*80.
const RING_THICKNESS = 80;
const UNTOPPED = "_untopped";

export function computePlacement(
  nodes: Node[],
  edges: { from: string; to: string }[],
): PlacementResult {
  if (nodes.length === 0) {
    return {
      positions: new Map(),
      rings: [],
      topicsByRing: new Map(),
      ringRadii: [...DEFAULT_RING_RADII],
      topicAngles: new Map(),
    };
  }

  // Idea-type nodes anchor at the origin and are exempt from the force
  // simulation. They land in the result `positions` map (so the
  // renderer can draw them) but NOT in the `placements` array that the
  // force loop iterates over — repulsion + ring gravity would otherwise
  // shove the idea around like any other node. Edges from other nodes
  // to the idea still create attraction (toward (0, 0)) because edge
  // attraction reads endpoints from the map by id.
  const ideaPlacements: Placement[] = [];
  const simNodes: Node[] = [];
  for (const n of nodes) {
    if (n.type === "idea") {
      ideaPlacements.push({
        id: n.id,
        x: 0,
        y: 0,
        vx: 0,
        vy: 0,
        ring: extractRing(n),
        topic: extractTopic(n),
        targetAngle: 0,
      });
    } else {
      simNodes.push(n);
    }
  }

  // Effective per-ring radii (computed on the simulation set only — the
  // idea's ring slot doesn't add density pressure to ring 0).
  const ringRadii = computeEffectiveRingRadii(simNodes);
  const ringRadiusOf = (r: number): number => {
    if (r < 0) return 0;
    if (r < ringRadii.length) return ringRadii[r];
    return ringRadii[ringRadii.length - 1] + (r - (ringRadii.length - 1)) * 160;
  };

  // Stable seeded RNG so every render produces the same layout for the
  // same input. Hash of all node ids feeds the seed.
  const rng = mulberry32(
    hashString(
      nodes
        .map((n) => n.id)
        .sort()
        .join(","),
    ),
  );

  // --- 1. Per-node (ring, topic) extraction -------------------------
  // placements = simulation set (excludes idea nodes). byId = full
  // result map (includes idea nodes at (0, 0)) so renderer + edge
  // lookups find them.
  const placements: Placement[] = simNodes.map((n) => ({
    id: n.id,
    x: 0,
    y: 0,
    vx: 0,
    vy: 0,
    ring: extractRing(n),
    topic: extractTopic(n),
    targetAngle: 0, // filled in step 3 once sectors are known
  }));
  const byId = new Map<string, Placement>();
  for (const p of placements) byId.set(p.id, p);
  for (const p of ideaPlacements) byId.set(p.id, p);

  // --- 2. Topic angular sectors per ring ----------------------------
  const ringTopics = new Map<number, Set<string>>();
  for (const p of placements) {
    let s = ringTopics.get(p.ring);
    if (!s) {
      s = new Set();
      ringTopics.set(p.ring, s);
    }
    s.add(p.topic);
  }
  const topicAngles = new Map<string, number>();
  const topicsByRing = new Map<number, string[]>();
  for (const [ring, set] of ringTopics.entries()) {
    const topics = [...set].sort();
    topicsByRing.set(ring, topics);
    const sectorSize = SECTOR_SPAN / Math.max(topics.length, 1);
    topics.forEach((t, i) => {
      topicAngles.set(`${ring}:${t}`, SECTOR_START + (i + 0.5) * sectorSize);
    });
  }

  // --- 3. Initial seed positions + per-node target angles -----------
  // Group by (ring, topic). Each node gets its own SLOT along the
  // topic's sector arc — not just a shared sector midpoint — so the
  // topic-sector pull (step 4 below) spreads them evenly along the arc
  // instead of collapsing them onto a single point. Stable sort by id
  // within the bucket keeps slots assigned consistently across reloads.
  const buckets = new Map<string, Placement[]>();
  for (const p of placements) {
    const key = `${p.ring}:${p.topic}`;
    let list = buckets.get(key);
    if (!list) {
      list = [];
      buckets.set(key, list);
    }
    list.push(p);
  }
  for (const [key, list] of buckets.entries()) {
    const [ringStr, topic] = key.split(":");
    const ring = parseInt(ringStr, 10);
    const baseRadius = ringRadiusOf(ring);
    const baseAngle = topicAngles.get(`${ring}:${topic}`) ?? 0;
    const numTopicsInRing = topicsByRing.get(ring)?.length ?? 1;
    const sectorSpan = SECTOR_SPAN / Math.max(numTopicsInRing, 1);
    // Use 80% of the sector for slots, leave 10% gap on each side so
    // adjacent topics' edge nodes don't quite touch at the boundary.
    const usableSpan = sectorSpan * 0.8;
    const slotSize =
      list.length <= 1 ? 0 : usableSpan / Math.max(list.length - 1, 1);
    list.sort((a, b) => a.id.localeCompare(b.id));
    list.forEach((p, i) => {
      const slotOffset =
        list.length === 1 ? 0 : (i - (list.length - 1) / 2) * slotSize;
      p.targetAngle = baseAngle + slotOffset;
      // Initial seed slightly jittered around the slot's target angle +
      // ring radius. Force sim refines from here.
      const angle = p.targetAngle + (rng() - 0.5) * 0.08;
      const r = baseRadius + (rng() - 0.5) * 30;
      p.x = Math.cos(angle) * r;
      p.y = Math.sin(angle) * r;
    });
  }

  // --- 4. Force simulation ------------------------------------------
  const edgeRefs = edges
    .map((e) => ({ a: byId.get(e.from), b: byId.get(e.to) }))
    .filter((e): e is { a: Placement; b: Placement } => !!e.a && !!e.b);

  for (let iter = 0; iter < ITERATIONS; iter++) {
    // 1. Pairwise repulsion
    for (let i = 0; i < placements.length; i++) {
      for (let j = i + 1; j < placements.length; j++) {
        const ni = placements[i];
        const nj = placements[j];
        const dx = nj.x - ni.x;
        const dy = nj.y - ni.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const force = REPULSION / (dist * dist);
        ni.vx -= (dx / dist) * force;
        ni.vy -= (dy / dist) * force;
        nj.vx += (dx / dist) * force;
        nj.vy += (dy / dist) * force;
      }
    }
    // 2. Edge attraction
    for (const e of edgeRefs) {
      const dx = e.b.x - e.a.x;
      const dy = e.b.y - e.a.y;
      const dist = Math.sqrt(dx * dx + dy * dy) || 1;
      const force = (dist - EDGE_REST_LENGTH) * EDGE_K;
      e.a.vx += (dx / dist) * force;
      e.a.vy += (dy / dist) * force;
      e.b.vx -= (dx / dist) * force;
      e.b.vy -= (dy / dist) * force;
    }
    // 3. Ring gravity
    for (const p of placements) {
      const target = ringRadiusOf(p.ring);
      const current = Math.sqrt(p.x * p.x + p.y * p.y) || 1;
      const force = (target - current) * RING_K;
      p.vx += (p.x / current) * force;
      p.vy += (p.y / current) * force;
    }
    // 4. Per-node tangential pull. Each node has its own targetAngle
    // (its slot within the topic sector); pull toward that, not the
    // sector midpoint, so nodes spread evenly along the arc instead
    // of collapsing onto each other.
    for (const p of placements) {
      const currentAngle = Math.atan2(p.y, p.x);
      let angleDiff = p.targetAngle - currentAngle;
      while (angleDiff > Math.PI) angleDiff -= Math.PI * 2;
      while (angleDiff < -Math.PI) angleDiff += Math.PI * 2;
      const currentR = Math.sqrt(p.x * p.x + p.y * p.y) || 1;
      const tx = -p.y / currentR;
      const ty = p.x / currentR;
      const strength = angleDiff * TOPIC_K;
      p.vx += tx * strength;
      p.vy += ty * strength;
    }
    // Apply + damp
    for (const p of placements) {
      p.x += p.vx * VELOCITY_STEP;
      p.y += p.vy * VELOCITY_STEP;
      p.vx *= DAMPING;
      p.vy *= DAMPING;
    }
    // 5. Hard radial clamp per ring. Without this, a crowded inner ring
    // (e.g. 30+ ring-0 nodes) generates enough cumulative repulsion to
    // push nodes outward past the next ring's guide circle — the ring
    // gravity coefficient cannot win against repulsion at small radii.
    // The clamp guarantees nodes stay in their ring's annulus visually,
    // even though that means the equilibrium isn't "natural" force-sim
    // equilibrium. Tradeoff: rings now READ as rings.
    //
    // Annulus thickness is RING_THICKNESS (50) on each side of the
    // target. Ring 0's inner bound is 0 (allowed to collapse to center).
    // Ring 1+'s inner bound is target-thickness so they don't fall into
    // ring 0's territory.
    for (const p of placements) {
      const target = ringRadiusOf(p.ring);
      const current = Math.sqrt(p.x * p.x + p.y * p.y) || 1;
      const ringMaxR = target + RING_THICKNESS;
      const ringMinR = p.ring === 0 ? 0 : Math.max(0, target - RING_THICKNESS);
      if (current > ringMaxR) {
        const factor = ringMaxR / current;
        p.x *= factor;
        p.y *= factor;
      } else if (current < ringMinR && current > 0) {
        const factor = ringMinR / current;
        p.x *= factor;
        p.y *= factor;
      }
    }
  }

  return {
    positions: byId,
    rings: [...ringTopics.keys()].sort((a, b) => a - b),
    topicsByRing,
    ringRadii,
    topicAngles,
  };
}

// Compute per-ring effective radii based on actual per-topic-sector
// density. Each ring's space is divided into N equal sectors (one per
// distinct topic in that ring); a ring's required radius is the radius
// at which the densest sector's nodes fit at NODE_ARC_PX arc length:
//
//   sectorRadians = SECTOR_SPAN / numTopicsInRing
//   maxCountPerTopic = max nodes in any single topic within this ring
//   required = NODE_ARC_PX * maxCountPerTopic / sectorRadians
//
// `effective[i] = max(default, required)`. Min-spacing is then enforced
// between ALL consecutive rings (ring 0 ↔ 1 included) so an expanded
// inner ring pushes outer rings outward, never overlapping into them.
function computeEffectiveRingRadii(nodes: Node[]): number[] {
  // Count nodes per (ring, topic) bucket; track distinct topics per ring.
  const ringTopicCounts = new Map<number, Map<string, number>>();
  for (const n of nodes) {
    const r = extractRing(n);
    if (r < 0 || r >= DEFAULT_RING_RADII.length) continue;
    const t = extractTopic(n);
    let topicMap = ringTopicCounts.get(r);
    if (!topicMap) {
      topicMap = new Map();
      ringTopicCounts.set(r, topicMap);
    }
    topicMap.set(t, (topicMap.get(t) ?? 0) + 1);
  }
  const effective = [...DEFAULT_RING_RADII] as number[];
  for (let i = 0; i < effective.length; i++) {
    const topicMap = ringTopicCounts.get(i);
    if (!topicMap || topicMap.size === 0) continue;
    const numTopics = topicMap.size;
    const sectorRadians = SECTOR_SPAN / numTopics;
    let maxCountPerTopic = 0;
    for (const c of topicMap.values()) {
      if (c > maxCountPerTopic) maxCountPerTopic = c;
    }
    const required = (NODE_ARC_PX * maxCountPerTopic) / sectorRadians;
    effective[i] = Math.max(DEFAULT_RING_RADII[i], required);
  }
  // Min spacing enforcement between ALL consecutive rings (ring 0 ↔ 1
  // included). With the idea pinned at center, ring 0 is free to grow
  // without colliding with the anchor; min-spacing here just keeps
  // rings visually distinct.
  for (let i = 1; i < effective.length; i++) {
    effective[i] = Math.max(effective[i], effective[i - 1] + MIN_RING_SPACING);
  }
  return effective;
}

function extractRing(n: Node): number {
  const fields = (n.fields ?? {}) as Record<string, unknown>;
  const r = fields.ring;
  if (typeof r === "number" && Number.isFinite(r)) return Math.max(0, Math.floor(r));
  if (typeof r === "string") {
    const parsed = parseInt(r, 10);
    if (Number.isFinite(parsed)) return Math.max(0, parsed);
  }
  return 0;
}

// REAL DATA: fields.topics is an array of topic IDs. Pick first for
// placement. Nodes with empty/missing topics land in UNTOPPED so they
// don't collapse to one angle (which would create a stack of
// zero-distance repulsion-bombs).
function extractTopic(n: Node): string {
  const fields = (n.fields ?? {}) as Record<string, unknown>;
  const topics = fields.topics;
  if (Array.isArray(topics) && topics.length > 0 && typeof topics[0] === "string") {
    return topics[0];
  }
  // Legacy fallback for hand-authored fixtures that use singular `topic`.
  if (typeof fields.topic === "string" && fields.topic.length > 0) return fields.topic;
  return UNTOPPED;
}

export function isUntopped(topic: string): boolean {
  return topic === UNTOPPED;
}

// Deterministic RNG so the layout is byte-identical across reloads of
// the same tree. mulberry32 from
// https://github.com/bryc/code/blob/master/jshash/PRNGs.md
function mulberry32(seed: number) {
  let t = seed >>> 0;
  return function () {
    t = (t + 0x6d2b79f5) >>> 0;
    let r = t;
    r = Math.imul(r ^ (r >>> 15), r | 1);
    r ^= r + Math.imul(r ^ (r >>> 7), r | 61);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

function hashString(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
